import { Icon } from '@natera/material/lib/icon'
import { cn } from '~/shared/lib/cn'
import type { AmountType, CodeGroupFormValues } from '../../../model'
import { AmountTypeToggle } from './amount-type-toggle'
import { CarcBadge } from './carc-badge'
import { CurrencyCell } from './currency-cell'

interface DeductionCellProps {
  /** Value in cents (integer) when dollars, raw number when percentage. */
  value: number | null
  onCentsChange: (cents: number | null) => void
  onBlur?: () => void
  disabled?: boolean
  codeGroups: CodeGroupFormValues[]
  onOpenSheet: () => void
  onClearCodes: () => void
  /** When true, "+" button shows even when cell is empty. Only adjustments column starts from the sidebar. */
  alwaysShowAddButton?: boolean
  /** When provided, renders a $/% toggle. */
  amountType?: AmountType
  onAmountTypeChange?: (type: AmountType) => void
}

/**
 * Currency input with a CARC badge trigger for the tagging side sheet.
 * Shows `+` when no CARCs assigned, or a badge like `CO16 (+2)` when codes exist.
 * Optionally renders a $/% toggle when amountType is provided.
 */
export function DeductionCell({
  value,
  onCentsChange,
  onBlur,
  disabled,
  codeGroups,
  onOpenSheet,
  onClearCodes,
  alwaysShowAddButton,
  amountType,
  onAmountTypeChange,
}: DeductionCellProps) {
  const groups = codeGroups ?? []
  const carcList = groups.flatMap((g) => g.carcs)
  const rarcList = groups.flatMap((g) => g.rarcs)
  const codesCount = carcList.length + rarcList.length
  const hasCodes = codesCount > 0
  const hasToggle = amountType !== undefined
  const showAdd = alwaysShowAddButton || value != null

  return (
    <div
      className={cn('flex items-center gap-1', {
        'min-w-[90px]': !hasToggle && showAdd && codesCount === 0,
        'min-w-[105px]': !hasToggle && showAdd && codesCount === 1,
        'min-w-[135px]': !hasToggle && showAdd && codesCount > 1,
        'min-w-[129px]': hasToggle && showAdd && codesCount === 0,
        'min-w-[144px]': hasToggle && showAdd && codesCount === 1,
        'min-w-[174px]': hasToggle && showAdd && codesCount > 1,
      })}
    >
      {hasToggle && (
        <AmountTypeToggle
          value={amountType}
          onChange={onAmountTypeChange!}
        />
      )}
      <div className="min-w-0 flex-1">
        <CurrencyCell
          value={value}
          onCentsChange={onCentsChange}
          isPercentage={amountType === 'percentage'}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            if (e.target.value === '' && hasCodes) onClearCodes()
            onBlur?.()
          }}
          disabled={disabled}
        />
      </div>
      {hasCodes ? (
        <CarcBadge
          carcList={carcList}
          rarcList={rarcList}
          onClick={onOpenSheet}
        />
      ) : alwaysShowAddButton || value != null ? (
        <button
          type="button"
          className="text-action-active hover:bg-action-focused focus-visible:bg-action-focused flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-transparent outline-none"
          onClick={onOpenSheet}
          title="Add CARC/RARC codes"
        >
          <Icon
            className="text-[20px]"
            children="add"
          />
        </button>
      ) : null}
    </div>
  )
}
